import React from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../theme/ThemeContext';
import { getMonthGrid, isSameDay, normalizeDateRange } from './utils';
import { formatDate } from '../../utils/dateFormat';

interface MiniMonthProps {
  monthAnchor: Date;
  setMonthAnchor: (d: Date) => void;
  selectedWeekStart: Date;
  selectedWeekEnd: Date;
  onDayClick: (d: Date) => void;
  onRangeSelect?: (start: Date, end: Date) => void;
  onToday: () => void;
}

export const MiniMonth: React.FC<MiniMonthProps> = ({
  monthAnchor,
  setMonthAnchor,
  selectedWeekStart,
  selectedWeekEnd,
  onDayClick,
  onRangeSelect,
  onToday
}) => {
  const { t, i18n } = useTranslation();
  const { tokens } = useTheme();
  const monthWeeks = React.useMemo(() => getMonthGrid(monthAnchor), [monthAnchor]);
  const [dragRange, setDragRange] = React.useState<{ start: Date; end: Date } | null>(null);
  const dragAnchorRef = React.useRef<Date | null>(null);
  const dragRangeRef = React.useRef<{ start: Date; end: Date } | null>(null);
  const movedRef = React.useRef(false);
  const activeRange = dragRange ?? { start: selectedWeekStart, end: selectedWeekEnd };
  const dayInSelectedWeek = (d: Date) => d >= activeRange.start && d <= activeRange.end;
  const goPrevMonth = () => setMonthAnchor(new Date(monthAnchor.getFullYear(), monthAnchor.getMonth() - 1, 1));
  const goNextMonth = () => setMonthAnchor(new Date(monthAnchor.getFullYear(), monthAnchor.getMonth() + 1, 1));

  const setCurrentDragRange = (range: { start: Date; end: Date } | null) => {
    dragRangeRef.current = range;
    setDragRange(range);
  };

  const beginDrag = (day: Date, button: number) => {
    if (button !== 0) return;
    dragAnchorRef.current = day;
    movedRef.current = false;
    setCurrentDragRange(normalizeDateRange(day, day));
  };

  const updateDrag = (day: Date) => {
    const anchor = dragAnchorRef.current;
    if (!anchor) return;
    if (!isSameDay(anchor, day)) movedRef.current = true;
    setCurrentDragRange(normalizeDateRange(anchor, day));
  };

  const finishDrag = React.useCallback(() => {
    const range = dragRangeRef.current;
    if (!dragAnchorRef.current || !range) return;

    if (movedRef.current && onRangeSelect) {
      onRangeSelect(range.start, range.end);
    } else {
      onDayClick(dragAnchorRef.current);
    }

    dragAnchorRef.current = null;
    dragRangeRef.current = null;
    movedRef.current = false;
    setCurrentDragRange(null);
  }, [onDayClick, onRangeSelect]);

  React.useEffect(() => {
    window.addEventListener('pointerup', finishDrag);
    window.addEventListener('pointercancel', finishDrag);
    return () => {
      window.removeEventListener('pointerup', finishDrag);
      window.removeEventListener('pointercancel', finishDrag);
    };
  }, [finishDrag]);
  
  const weekDays = React.useMemo(() => {
    const base = new Date(2023, 0, 2); // Jan 2 2023 is a Monday
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(base);
      d.setDate(base.getDate() + i);
      return d.toLocaleDateString(i18n.language, { weekday: 'short' });
    });
  }, [i18n.language]);

  return (
    <div style={{ width: 240, flexShrink: 0, color: tokens.app.text }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <strong data-testid="mini-month-label" style={{ fontSize: 16 }}>{monthAnchor.toLocaleString(i18n.language, { month: 'long', year: 'numeric' })}</strong>
        <div style={{ display: 'flex', gap: 4 }}>
          <button data-testid="mini-month-previous" onClick={goPrevMonth} style={{ padding: '2px 6px', background: tokens.app.surfaceAlt, color: tokens.app.text, border: 'none', borderRadius: 4, cursor: 'pointer' }}>{'\u2039'}</button>
          <button onClick={onToday} style={{ padding: '2px 6px', background: tokens.app.surfaceAlt, color: tokens.app.text, border: 'none', borderRadius: 4, cursor: 'pointer' }}>{t('calendar.today')}</button>
          <button data-testid="mini-month-next" onClick={goNextMonth} style={{ padding: '2px 6px', background: tokens.app.surfaceAlt, color: tokens.app.text, border: 'none', borderRadius: 4, cursor: 'pointer' }}>{'\u203a'}</button>
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', fontSize: 11, color: tokens.app.muted, marginBottom: 4 }}>
        {weekDays.map(d => <div key={d} style={{ textAlign: 'center', fontWeight: 600 }}>{d}</div>)}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: 2 }}>
        {monthWeeks.map(week => week.map((d) => {
          const inCurrentMonth = d.getMonth() === monthAnchor.getMonth();
          const today = new Date();
          const isToday = isSameDay(d, today);
          const inSelected = dayInSelectedWeek(d);
          return (
            <div
              key={d.toISOString()}
              data-testid={`mini-month-day-${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`}
              onPointerDown={(event) => {
                event.preventDefault();
                beginDrag(d, event.button);
              }}
              onPointerEnter={() => updateDrag(d)}
              onPointerUp={finishDrag}
              onMouseDown={(event) => {
                event.preventDefault();
                beginDrag(d, event.button);
              }}
              onMouseEnter={() => updateDrag(d)}
              onMouseUp={finishDrag}
              style={{
                cursor: 'pointer',
                userSelect: 'none',
                touchAction: 'none',
                textAlign: 'center',
                padding: '4px 0',
                borderRadius: 6,
                background: inSelected ? tokens.app.primary : isToday ? tokens.app.activeBg : 'transparent',
                color: inSelected ? tokens.app.primaryText : inCurrentMonth ? tokens.app.text : tokens.app.muted,
                fontWeight: isToday || inSelected ? 600 : 400,
                border: isToday && !inSelected ? `1px solid ${tokens.app.primary}` : '1px solid transparent'
              }}
              title={formatDate(d)}
            >{d.getDate()}</div>
          );
        }))}
      </div>
      <hr style={{ margin: '12px 0', borderColor: tokens.app.border }} />
    </div>
  );
};
